import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
            <i className="ri-file-unknow-line text-2xl text-secondary-500"></i>
          </div>
          <h2 className="text-xl font-bold font-heading text-foreground-950 mb-2">文章未找到</h2>
          <p className="text-sm text-foreground-500 mb-4">您访问的文章不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.thumbnail,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '夜桜导航',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://yozakura-nav.example.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.category, href: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 lg:px-8 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-block text-xs text-primary-600 bg-primary-100/70 px-2.5 py-1 rounded-md hover:bg-primary-200 transition-colors mb-4 whitespace-nowrap"
              >
                {article.category}
              </Link>
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-time-line"></i>
                  {article.readTime} 分钟阅读
                </span>
              </div>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.thumbnail}
                alt={article.title}
                title={`${article.title} - 里番动漫推荐`}
                className="w-full h-64 md:h-96 object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center gap-2 mt-10 pt-8 border-t border-background-200/70 flex-wrap">
              <span className="text-sm text-foreground-500 whitespace-nowrap">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-secondary-700 bg-secondary-100/80 px-2 py-1 rounded whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 lg:px-8 py-14 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-xl font-bold font-heading text-foreground-950 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all"
                  >
                    <img
                      src={ra.thumbnail}
                      alt={ra.title}
                      className="w-full h-36 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="p-3">
                      <h4 className="text-sm font-semibold font-heading text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {ra.title}
                      </h4>
                      <span className="text-xs text-foreground-400 mt-2 block whitespace-nowrap">
                        {ra.date}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}